import React, { useEffect, useMemo, useState, forwardRef,
  useImperativeHandle } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  StyleSheet,
  Platform,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import DOBTextInputComponent from "../../../common/DOBTextInputComponent";
import {
  validateDobDateFromDisplayString,
  formatDobInput,
  dateFromDobDisplayString,
  isDobDisplayComplete,
  DOB_MIN_AGE_RECEIVE,
  DOB_MAX_AGE_YEARS,
} from "../../../../../helper/dobValidation";

const RECEIVER_DOB_AGE_OPTIONS = {
  minimumAgeYears: DOB_MIN_AGE_RECEIVE,
  maximumAgeYears: DOB_MAX_AGE_YEARS,
} as const;

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

export interface ReceiverDOBStepHandle {
  submit: () => Promise<boolean>;
}

const ReceiverDOBStep = forwardRef<ReceiverDOBStepHandle, Props>(({ onValidationChange }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
    dob: "",
  });
  const storedDobString = useSessionStore((s: SessionState) => (s as any).RecieverDOBString) || "";
  const [dob, setDob] = useState(
    () => (useSessionStore.getState() as any).RecieverDOBString || ""
  );
  const [showPicker, setShowPicker] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const setRecieverDOBString = useSessionStore((s: SessionState) => (s as any).setRecieverDOBString);
  const setRecieverDOB = useSessionStore((s: SessionState) => (s as any).setRecieverDOB);

  const isValid = useMemo(() => {
    if (!isDobDisplayComplete(dob)) return false;
    return !validateDobDateFromDisplayString(t, dob.trim(), true, RECEIVER_DOB_AGE_OPTIONS);
  }, [dob, t]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // expose validate function
  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (!dob.trim()) {
        const next = { dob: t("SENDER_DOB_ERROR_REQUIRED") };
        setErrors(next);
        return false;
      }
      if (isDobDisplayComplete(dob)) {
        const err = validateDobDateFromDisplayString(t, dob.trim(), true, RECEIVER_DOB_AGE_OPTIONS);
        const next = { dob: err };
        setErrors(next);
        return !Boolean(err);
      }
      const next = { dob: t("SENDER_DOB_ERROR_INVALID") };
      setErrors(next);
      return false;
    },
  }));
  
    
  // Keep local DOB in sync when store is hydrated (e.g., after AuthConsumer)
  useEffect(() => {
    setDob(storedDobString || "");
  }, [storedDobString]);

  // Mock ID verification function (replace with real API call)
  const verifyDOBWithID = async (enteredDob: string) => {
    // Simulate backend check with previously uploaded ID
    return new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(true), 1000) // always returns true for now
    );
  };

  const handleDateChange = async (_: any, selectedDate?: Date) => {
    setShowPicker(false);
    if (selectedDate) {
      const formatted = formatDate(selectedDate);
      setDob(formatted);

      // Call background verification
      const isValid = await verifyDOBWithID(formatted);
      // if (isValid) {
      //   setShowConfirmModal(true);
      // }
    }
  };

  const handleManualInput = async (rawText: string) => {
    const formatted = formatDobInput(rawText);
    setDob(formatted);
    try { setRecieverDOBString?.(formatted); } catch {}
    if (isDobDisplayComplete(formatted)) {
      const err = validateDobDateFromDisplayString(t, formatted, true, RECEIVER_DOB_AGE_OPTIONS);
      setErrors((prev) => ({ ...prev, dob: err }));
      if (!err) {
        const parsed = dateFromDobDisplayString(formatted);
        if (parsed) {
          try { setRecieverDOB?.(parsed); } catch {}
          await verifyDOBWithID(formatted);
        }
      }
    } else if (!formatted.trim()) {
      setErrors((prev) => ({ ...prev, dob: t("SENDER_DOB_ERROR_REQUIRED") }));
    } else {
      setErrors((prev) => ({ ...prev, dob: "" }));
    }
  };

  const formatDate = (date: Date) => {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("SENDER_DOB_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_DOB_SUBTITLE")}</Text>
      <DOBTextInputComponent
        testID="receiverDOB"
        isAutoFocus={true}
        value={dob}
        onChangeText={handleManualInput}
        errorText={errors.dob}
        onClearError={() => setErrors((prev) => ({ ...prev, dob: "" }))}
        keyboardType="number-pad"
        maxLength={10}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24,
  }
});

export default ReceiverDOBStep;